/**
 * SectionMapper v2 — customer-first IT Act 1961 → 2025 explorer.
 *
 * Designed for salaried / self-employed users who are NOT tax professionals.
 * The main interaction model is:
 *
 *   1. Scan the slim hero ("what is this page").
 *   2. Either type into the big search bar, OR pick a topic tile.
 *   3. Skim the simplified card list.
 *   4. Click a card to expand details inline.
 *
 * No floating ribbons, no 4-cell stat strip, no 15-category bar. The
 * complexity is hidden behind progressive disclosure (one click) instead of
 * being shown all at once.
 */
import * as React from 'react';
import { Search, X, ArrowRight, Sparkles, ShieldCheck, Hash, AlertCircle } from 'lucide-react';
import { MAPPING_DATA, getSectionFuse } from '../data';
import type { Section } from '../types';
import { TOPICS, findTopic, type Topic } from '../topics';
import { SectionCard } from './section-card';

// ── Palette ───────────────────────────────────────────────────────────────────
const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';
const CREAM  = 'hsl(var(--palette-cream,   40 100% 98%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

const QUICK_CHIPS = ['80C', 'HRA', '87A', '194C', 'Capital gains'];

// ── Component ─────────────────────────────────────────────────────────────────

export function SectionMapper() {
  const [rawQuery, setRawQuery]   = React.useState('');     // controlled input
  const [query, setQuery]         = React.useState('');     // debounced query
  const [activeTopic, setTopic]   = React.useState<string | null>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Debounce — keeps the list calm while the user is still typing.
  React.useEffect(() => {
    const id = setTimeout(() => setQuery(rawQuery.trim()), 200);
    return () => clearTimeout(id);
  }, [rawQuery]);

  const fuse = React.useMemo(() => getSectionFuse(), []);
  const topic = findTopic(activeTopic);
  const isSearching = query.length >= 2;
  const isBrowsing  = !isSearching && topic !== null;
  const showHero    = !isSearching && !isBrowsing;

  // Apply search → then topic filter (whichever the user picked).
  const results = React.useMemo<Section[]>(() => {
    let list: Section[] = isSearching
      ? fuse.search(query).map((r) => r.item)
      : MAPPING_DATA.sections;
    if (topic) list = list.filter((s) => topic.match(s));
    return list;
  }, [query, fuse, topic, isSearching]);

  // Counts for each topic tile — drives the badges shown at rest.
  const topicCounts = React.useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const t of TOPICS) {
      map[t.id] = MAPPING_DATA.sections.filter((s) => t.match(s)).length;
    }
    return map;
  }, []);

  // Highlights for the "What's new in 2025" strip — limit-changed sections.
  const limitChanges = React.useMemo<Section[]>(
    () => MAPPING_DATA.sections.filter((s) => s.limit_changed).slice(0, 4),
    [],
  );

  // ⌘K / Ctrl-K shortcut to focus the search input.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        requestAnimationFrame(() => searchRef.current?.focus());
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function handleSuggestion(text: string) {
    setRawQuery(text);
    setQuery(text);          // skip debounce for click-driven changes
    setTopic(null);
    requestAnimationFrame(() => searchRef.current?.focus());
  }

  function handleTopicClick(id: string) {
    setTopic((prev) => (prev === id ? null : id));
    setRawQuery('');
    setQuery('');
  }

  function clearAll() {
    setRawQuery('');
    setQuery('');
    setTopic(null);
  }

  return (
    <div className="min-h-[calc(100vh-4rem)]" style={{ backgroundColor: CREAM }}>

      {/* ── Slim hero ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: FOREST }}>
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-20 animate-float-slow"
          style={{ backgroundColor: AMBER }}
        />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-10 relative">
          <h1
            className="font-display font-semibold text-3xl sm:text-4xl xl:text-5xl tracking-tight leading-tight max-w-3xl animate-slide-up-fade"
            style={{ color: CREAM }}
          >
            Income Tax Act, 2025 — <em className="italic" style={{ color: AMBER }}>simplified.</em>
          </h1>
          <p
            className="text-sm sm:text-base leading-relaxed mt-2 max-w-2xl animate-slide-up-fade stagger-1"
            style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.78)' }}
          >
            Look up any section, deduction, or term — explained in plain English.
          </p>

          <div className="mt-5 animate-slide-up-fade stagger-2">
            <SearchInput
              value={rawQuery}
              onChange={setRawQuery}
              onClear={clearAll}
              inputRef={searchRef}
            />
          </div>

          {/* Quick chips — one-tap shortcuts to common questions */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5 animate-slide-up-fade stagger-3">
            <span
              className="text-[10px] font-semibold uppercase tracking-widest mr-1"
              style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.55)' }}
            >
              Try
            </span>
            {QUICK_CHIPS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => handleSuggestion(q)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-all hover:scale-[1.03] active:scale-95"
                style={{
                  backgroundColor: 'hsl(var(--palette-cream, 40 100% 98%) / 0.10)',
                  color:           CREAM,
                  border:          'hsl(var(--palette-cream, 40 100% 98%) / 0.18) 1px solid',
                }}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Tiny metadata line — not a stat strip, just a single trust signal */}
          <p
            className="text-[11px] mt-4 inline-flex items-center gap-2 animate-slide-up-fade stagger-4"
            style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.55)' }}
          >
            <ShieldCheck className="h-3 w-3" aria-hidden />
            {MAPPING_DATA.sections.length} sections · CBDT-verified mappings ·
            Tax Year 2026-27
          </p>
        </div>
      </section>

      {/* ── Browse mode (rest state) — topic tiles + "what's new" strip ─── */}
      {showHero && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
          <section>
            <SectionHeader
              eyebrow="Browse by topic"
              title="What are you trying to figure out?"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {TOPICS.map((t, idx) => (
                <TopicTile
                  key={t.id}
                  topic={t}
                  count={topicCounts[t.id] ?? 0}
                  onClick={() => handleTopicClick(t.id)}
                  index={idx}
                />
              ))}
            </div>
          </section>

          {limitChanges.length > 0 && (
            <section>
              <SectionHeader
                eyebrow="What's new in 2025"
                title="Limit changes worth knowing"
                hint="The most common reason a return needs updating."
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {limitChanges.map((s, idx) => (
                  <LimitChangeCard
                    key={s.id}
                    section={s}
                    index={idx}
                    onClick={() => handleSuggestion(s.old_ref)}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* ── Results mode — search OR topic filter active ─────────────────── */}
      {!showHero && (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-5 sm:py-6">

          {/* Active filter strip */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{results.length}</span>{' '}
              {results.length === 1 ? 'section' : 'sections'}
              {isSearching && (
                <> matching <span className="font-mono font-semibold text-foreground">"{query}"</span></>
              )}
              {!isSearching && topic && (
                <> in <span className="font-semibold text-foreground">{topic.label}</span></>
              )}
            </p>
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 text-xs font-semibold transition-colors hover:opacity-70"
              style={{ color: FOREST }}
            >
              <X className="h-3 w-3" /> Clear
            </button>
          </div>

          {/* Results */}
          {results.length > 0 ? (
            <div className="flex flex-col gap-3">
              {results.map((s, i) => (
                <SectionCard key={s.id} section={s} index={i} />
              ))}
            </div>
          ) : (
            <EmptyState query={query} onSuggest={handleSuggestion} onBrowse={clearAll} />
          )}
        </div>
      )}

      {/* ── Disclaimer + footer ─────────────────────────────────────────── */}
      <footer className="mx-auto max-w-5xl px-4 sm:px-6 py-6 mt-2 border-t border-border">
        <div
          className="flex items-start gap-2 rounded-xl px-3 py-2 mb-3"
          style={{ backgroundColor: MINT }}
        >
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" style={{ color: FOREST }} aria-hidden />
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            {MAPPING_DATA.meta.disclaimer}
          </p>
        </div>
        <p className="text-[11px] text-center text-muted-foreground">
          Data sourced from CBDT official concordance · Always verify with a qualified CA.
        </p>
      </footer>
    </div>
  );
}

// ── SearchInput — the page's hero element ─────────────────────────────────────

function SearchInput({
  value, onChange, onClear, inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
  return (
    <div
      className="relative flex items-center rounded-2xl shadow-md transition-shadow focus-within:shadow-lg"
      style={{ backgroundColor: CREAM }}
    >
      <Search className="h-5 w-5 text-muted-foreground ml-4 shrink-0" aria-hidden />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search a section, deduction, or keyword…"
        aria-label="Search the Income Tax Act"
        className="flex-1 px-3 py-3.5 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          className="mr-2 h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <span className="hidden sm:flex items-center gap-1 mr-3 text-[11px] text-muted-foreground">
        <kbd className="rounded border border-border bg-muted/50 px-1.5 py-0.5 font-sans font-semibold">
          {isMac ? '⌘' : 'Ctrl'}
        </kbd>
        <kbd className="rounded border border-border bg-muted/50 px-1.5 py-0.5 font-sans font-semibold">
          K
        </kbd>
      </span>
    </div>
  );
}

// ── Topic tile — replaces the 15-category bar ────────────────────────────────

function TopicTile({
  topic, count, onClick, index,
}: {
  topic: Topic;
  count: number;
  onClick: () => void;
  index: number;
}) {
  const Icon = topic.Icon;
  const stagger = `stagger-${Math.min(index + 1, 8)}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group rounded-2xl border border-border bg-background p-4 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/15 animate-slide-up-fade ${stagger}`}
    >
      <div
        className="h-10 w-10 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
        style={{ backgroundColor: MINT }}
      >
        <Icon className="h-5 w-5" style={{ color: FOREST }} aria-hidden />
      </div>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <h3 className="font-display font-semibold text-base text-foreground tracking-tight">
          {topic.label}
        </h3>
        <span
          className="text-[10px] font-bold rounded-full px-1.5 py-0.5"
          style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
        >
          {count}
        </span>
      </div>
      <p className="text-xs text-muted-foreground leading-snug">{topic.description}</p>
      <span
        className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold transition-transform"
        style={{ color: FOREST }}
      >
        Explore
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </span>
    </button>
  );
}

// ── Limit-change card — small horizontal highlight at rest ───────────────────

function LimitChangeCard({
  section, index, onClick,
}: {
  section: Section;
  index: number;
  onClick: () => void;
}) {
  const stagger = `stagger-${Math.min(index + 1, 8)}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group rounded-2xl border p-4 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 animate-slide-up-fade ${stagger}`}
      style={{
        backgroundColor: `${AMBER}08`,
        borderColor:     `${AMBER}40`,
      }}
    >
      <div className="flex items-start gap-2 mb-2">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: AMBER }} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: AMBER }}>
            Limit changed
          </p>
          <h4 className="font-display font-semibold text-sm text-foreground leading-tight mt-0.5">
            Section {section.old_ref} — {section.new_title || section.old_title}
          </h4>
        </div>
      </div>
      {section.old_limit != null && section.new_limit != null && (
        <p className="text-xs font-mono text-muted-foreground">
          ₹{section.old_limit.toLocaleString('en-IN')}{' '}
          <span style={{ color: FOREST }} className="font-bold">→</span>{' '}
          <span className="font-bold" style={{ color: FOREST }}>
            ₹{section.new_limit.toLocaleString('en-IN')}
          </span>
        </p>
      )}
      {section.limit_changed_note && (
        <p className="text-xs text-muted-foreground leading-snug mt-1.5 line-clamp-2">
          {section.limit_changed_note}
        </p>
      )}
      <span
        className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold transition-transform"
        style={{ color: FOREST }}
      >
        See details
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </span>
    </button>
  );
}

// ── Section header used inside browse mode ───────────────────────────────────

function SectionHeader({
  eyebrow, title, hint,
}: {
  eyebrow: string;
  title:   string;
  hint?:   string;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: AMBER }}>
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" aria-hidden />
          {eyebrow}
        </span>
      </p>
      <h2 className="font-display font-semibold text-xl sm:text-2xl text-foreground tracking-tight mt-1">
        {title}
      </h2>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({
  query, onSuggest, onBrowse,
}: {
  query: string;
  onSuggest: (q: string) => void;
  onBrowse: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 animate-fade-in">
      <div
        className="h-14 w-14 rounded-full flex items-center justify-center mb-4"
        style={{ backgroundColor: MINT }}
      >
        <Hash className="h-6 w-6" style={{ color: FOREST }} aria-hidden />
      </div>
      <p className="text-base font-semibold text-foreground mb-1">
        Nothing matches &ldquo;{query}&rdquo;
      </p>
      <p className="text-sm text-muted-foreground mb-5 max-w-sm">
        Try a section number, a tax term, or browse by topic.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
        {['80C', 'HRA', 'TDS', 'Capital gains', '194C'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSuggest(s)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all active:scale-95 hover:-translate-y-px"
            style={{ borderColor: 'hsl(var(--border))', color: FOREST }}
          >
            {s}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onBrowse}
        className="text-xs font-semibold underline underline-offset-4 hover:opacity-70 transition-opacity"
        style={{ color: FOREST }}
      >
        Browse all topics
      </button>
    </div>
  );
}
